import { order as createOrderNode, query as createQueryNode } from "../scripting/m-script/mscript-ast.js";
import { QLangCompiler } from "../scripting/q-lang/QLangCompiler.js";
import { QLangParser } from "../scripting/q-lang/QLangParser.js";
import { getConfig as getAppConfig } from "../../../config.js";

function resolveValue(value, context) {
    return typeof value === 'function' ? value.call(context) : value;
}

function getConfig(parent = null) {
    if (parent && typeof parent.getConfig === 'function')
        return parent.getConfig();
    if (parent && typeof parent.getApp === 'function')
        return parent.getApp().getConfig();
    return getAppConfig();
}

function normalizeUInt(value) {
    if (value === null || value === undefined || value === '')
        return null;

    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed) || parsed < 0)
        return null;

    return parsed;
}

export class DataQueryManager {

    static create(parent, options = {}) {
        return new DataQueryManager(parent, options);
    }

    constructor(parent, options = {}) {

        const config = getConfig(parent);
        const paginator = config?.paginator || {};

        const {
            fields = [],
            variables = [],
            onQueryChange = () => { /* do nothing */ },
            defaultQueryString = `$limit=${paginator.defaultPageSize || 25}`,
            minQueryLimit = paginator.minPageSize || 10,
            maxQueryLimit = paginator.maxPageSize || 100,
        } = options;

        this.parent = parent;

        this.fields = fields;
        this.variables = variables;
        this.onQueryChange = onQueryChange;
        this.defaultQueryString = defaultQueryString;
        this.minQueryLimit = minQueryLimit;
        this.maxQueryLimit = maxQueryLimit;
        this.queryStringParser = QLangParser.create();
        this.queryStringCompiler = QLangCompiler.create();

        this.query = null;
        this.queryErrors = null;

        this.setQueryString(this.getDefaultQueryString());
    }

    getParent() {
        return this.parent;
    }

    getFields() {
        return resolveValue(this.fields, this);
    }

    getVariables() {
        return resolveValue(this.variables, this);
    }

    getParser() {
        return this.queryStringParser;
    }

    getCompiler() {
        return this.queryStringCompiler;
    }

    getQuery() {
        return this.query || null;
    }

    getQueryFilter() {
        return this.query?.filter || null;
    }

    getQueryOrder() {
        return this.query?.order || null;
    }

    getQueryOffset() {
        return this.query?.offset ?? null;
    }

    getQueryLimit() {
        return this.query?.limit ?? null;
    }

    getQueryErrors() {
        return this.queryErrors || null;
    }

    getMinQueryLimit() {
        return resolveValue(this.minQueryLimit, this);
    }

    getMaxQueryLimit() {
        return resolveValue(this.maxQueryLimit, this);
    }

    getDefaultQueryString() {
        return resolveValue(this.defaultQueryString, this);
    }

    setQueryParams(queryParams) {

        this.query = null;
        this.queryErrors = null;

        if (!queryParams) {
            this.query = this.normalizeQuery(createQueryNode());
            return true;
        }

        const parser = this.getParser();

        const filter = parser.parseFilter(queryParams.filter ?? null);
        if (filter === false) {
            this.queryErrors = parser.getErrors();
            return false;
        }

        const order = parser.parseOrder(queryParams.order ?? null);
        if (order === false) {
            this.queryErrors = parser.getErrors();
            return false;
        }

        this.query = this.normalizeQuery(createQueryNode(
            filter,
            order,
            normalizeUInt(queryParams.limit),
            normalizeUInt(queryParams.offset),
        ));

        return true;
    }

    setQueryString(queryString) {

        this.query = null;
        this.queryErrors = null;

        const query = this.getParser().parse(queryString || null);
        if (query === false) {
            this.queryErrors = this.getParser().getErrors();
            return false;
        }

        this.query = this.normalizeQuery(query || createQueryNode());
        return true;
    }

    changeQueryString(queryString) {
        this.setQueryString(queryString);
        this.queryChanged();
    }

    queryChanged() {
        if (typeof this.onQueryChange === 'function')
            this.onQueryChange.call(this);
    }

    getQueryString(options) {
        if (!this.query)
            return null;

        return this.getCompiler().compile(this.query, options);
    }

    getDataQueryString(options) {
        return this.getQueryString(options);
    }

    clearQueryString() {
        this.setQueryString(null);
        this.setQueryOffset(0);
    }

    resetQueryString() {
        this.setQueryString(this.getDefaultQueryString());
        this.setQueryOffset(0);
    }

    applyQuery() {
        this.setQueryOffset(0);
        this.queryChanged();
    }

    clearQuery() {
        this.clearQueryString();
        this.queryChanged();
    }

    resetQuery() {
        this.resetQueryString();
        this.queryChanged();
    }

    setQueryOffset(value) {
        if (!this.query)
            return;
        this.query.offset = normalizeUInt(value) ?? 0;
    }

    changeQueryOffset(value) {
        this.setQueryOffset(value);
        this.queryChanged();
    }

    setQueryLimit(value) {
        if (!this.query)
            return;
        this.query.limit = this.normalizeLimit(value);
    }

    changeQueryLimit(value) {
        this.setQueryLimit(value);
        this.queryChanged();
    }

    setQueryOrder(value) {
        if (!this.query)
            return;
        this.query.order = Array.isArray(value) ? value : null;
    }

    changeQueryOrder(value) {
        this.setQueryOrder(value);
        this.queryChanged();
    }

    updateOrder(fieldName, additive = false, omitChangeNotification = false) {

        if (!this.query)
            return;

        let item = null;
        let index = -1;

        this.query.order = this.query.order || [];

        for (let i = 0; i < this.query.order.length; i++) {
            if (this.query.order[i].ident !== fieldName)
                continue;
            item = this.query.order[i];
            index = i;
            break;
        }

        if (additive) {
            if (item === null) {
                this.query.order.push(createOrderNode(fieldName, false));
            } else {
                item.direction = item.direction === 'asc' ? 'desc' : 'asc';
            }
        } else {
            if (item === null) {
                item = createOrderNode(fieldName, false);
            } else {
                item.direction = item.direction === 'asc' ? 'desc' : 'asc';
            }
            this.query.order = [item];
        }

        if (index < 0 && item !== null && !this.query.order.includes(item) && additive)
            this.query.order.push(item);

        if (!omitChangeNotification)
            this.queryChanged();
    }

    setQueryFilter(value) {
        if (!this.query)
            return;
        this.query.filter = value;
    }

    changeQueryFilter(value) {
        this.setQueryFilter(value);
        this.queryChanged();
    }

    normalizeQuery(query) {
        const normalized = query || createQueryNode();
        normalized.order = Array.isArray(normalized.order) ? normalized.order : null;
        normalized.offset = normalizeUInt(normalized.offset) ?? 0;
        normalized.limit = this.normalizeLimit(normalized.limit);
        return normalized;
    }

    normalizeLimit(value) {
        const config = getConfig();
        const fallback = config?.paginator?.defaultPageSize || 25;
        const min = this.getMinQueryLimit() ?? fallback;
        const max = this.getMaxQueryLimit() ?? fallback;
        const parsed = normalizeUInt(value) ?? fallback;
        return Math.min(Math.max(parsed, min), max);
    }

}
